import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getClassData, getTeacherData } from './api';
import { postAws } from '../postAws';

const StudentRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const selectedClass = useSelector((state) => state.teacherSub.selectedClass);
  const selectedSubject = useSelector((state) => state.teacherSub.selectedSubject);
  const teacherId = useSelector((state) => state.teacherSub.teacherId);
  const classCode = useSelector(state => state.teacherSub.classCode);

  useEffect(() => {
    const fetchRequests = async () => {
      if (!teacherId || !selectedClass || !selectedSubject || !classCode) {
        console.log('Missing required data, not fetching');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // First, get the teacher data to find the class ID
        const teacherResponse = await getTeacherData();
        if (teacherResponse.statusCode !== 200 || !teacherResponse.body || !teacherResponse.body.data) {
          throw new Error('Invalid response from teacher API');
        }
        
        const teacherData = teacherResponse.body.data;
        const selectedClassData = teacherData.createdClasses.find(
          classData => classData.classCode === classCode
        );

        if (!selectedClassData) {
          throw new Error('Selected class not found in teacher data');
        }

        // Now, fetch the class data using the class ID
        const classResponse = await getClassData(selectedClassData.classId);
        if (classResponse.statusCode !== 200 || !classResponse.body || !classResponse.body.data) {
          throw new Error('Invalid response from class API');
        }

        const classData = classResponse.body.data;
        setRequests(classData.enrollmentRequest || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching enrollment requests:', err);
        setError('Failed to fetch enrollment requests. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [teacherId, selectedClass, selectedSubject, classCode]);

  const handleApprove = async (studentId, classCode, _id) => {
    // TODO: Implement actual approval logic when backend is ready
    console.log(`Approving student with ID: ${classCode}`);
    const approveData = {
      body: {
        _id: _id,
        classCode: classCode,
        studentId: studentId
      },
      request: "approve"
    }
    await postAws('https://api-dev.aurait.ai/enrollment', "POST", approveData);
    setRequests(requests.filter(request => request.studentId !== studentId));
  };

  const handleDecline = async (studentId,  classCode, _id) => {
    // TODO: Implement actual decline logic when backend is ready
    console.log(`Declining student with ID: ${studentId}`);
    const declinedData = {
      body: {
        _id: _id,
        classCode: classCode,
        studentId: studentId
      },
      request: "decline"
    }
    await postAws('https://api-dev.aurait.ai/enrollment', "POST", declinedData);
    setRequests(requests.filter(request => request.studentId !== studentId));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!teacherId || !selectedClass || !selectedSubject || !classCode) {
    return (
      <div className="text-center mt-8 text-gray-600">
        Please select a class and subject to view enrollment requests.
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center mt-8 text-red-600 bg-red-100 p-4 rounded-lg">
        <p className="font-bold">Error:</p>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">
        Enrollment Requests for {selectedClass} - {selectedSubject}
      </h2>
      {requests.length === 0 ? (
        <p className="text-lg text-gray-600">No pending enrollment requests.</p>
      ) : (
        <ul className="space-y-4">
          {requests.map((request) => (
            <li key={request.studentId} className="bg-white shadow-lg rounded-lg p-6 transition duration-300 ease-in-out hover:shadow-xl">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-semibold text-xl text-gray-800">{request.studentName}</p>
                  <p className="text-sm text-gray-600">Email: {request.studentEmail}</p>
                  <p className="text-sm text-gray-600">
                    Requested: {new Date(request.createdAt).toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600">Status: {request.status}</p>
                </div>
                <div className="space-x-2">
                  <button
                    onClick={() => handleApprove(request.studentId, request.classCode, request._id)}
                    className="px-6 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleDecline(request.studentId, request.classCode, request._id)}
                    className="px-6 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                  >
                    Decline
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default StudentRequests;

